/**
 * PublicReschedulePage (08/2026) — /umbuchen/:token
 * Kunde bucht seinen verschobenen Termin OHNE Login um: Datum wählen →
 * (optional anderer Trainer) → freien Slot antippen → bestätigen.
 * Nutzt bestehende öffentliche Endpoints:
 *   GET /api/public/reschedule/{token}
 *   GET /api/portal/booking/trainers?service_id=…
 *   GET /api/portal/booking/availability?service_id=…&date=…&trainer_id=…
 *   POST /api/public/reschedule/{token}/confirm
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CalendarClock, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const nextDays = (count = 21) => {
  const days = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, 12);
    days.push(d);
  }
  return days;
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

export default function PublicReschedulePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [selTrainer, setSelTrainer] = useState(null);
  const [selDate, setSelDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selSlot, setSelSlot] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/reschedule/${token}`)
      .then((r) => {
        setInfo(r.data);
        setSelTrainer(r.data.old_trainer_id || 'any');
        if (r.data.service_id) {
          axios.get(`${API}/portal/booking/trainers`, { params: { service_id: r.data.service_id } })
            .then((tr) => setTrainers(Array.isArray(tr.data) ? tr.data : tr.data?.trainers || []))
            .catch(() => {});
        }
      })
      .catch((e) => setError(e.response?.data?.detail || 'Dieser Link ist ungültig.'));
  }, [token]);

  const loadSlots = useCallback(async (dateStr, trainerId) => {
    setSlotsLoading(true);
    setSlots(null);
    setSelSlot(null);
    try {
      const params = { service_id: info.service_id, date: dateStr };
      if (trainerId && trainerId !== 'any') params.trainer_id = trainerId;
      if (info.appointment_id) params.exclude_appointment_id = info.appointment_id;
      const res = await axios.get(`${API}/portal/booking/availability`, { params });
      const all = [
        ...(res.data?.morning_slots || []),
        ...(res.data?.afternoon_slots || []),
        ...(res.data?.slots || []),
      ];
      setSlots(all.filter((s) => s.available));
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [info]);

  useEffect(() => {
    if (info && selDate) loadSlots(selDate, selTrainer);
  }, [selDate, selTrainer, info, loadSlots]);

  const confirm = async () => {
    if (!selSlot) return;
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/public/reschedule/${token}/confirm`, {
        start_time: selSlot.datetime,
        trainer_id: selTrainer !== 'any' ? selTrainer : (selSlot.available_trainer_ids?.[0] || null),
      });
      setDone(res.data);
    } catch (e) {
      setError(null);
      alert(e.response?.data?.detail || 'Umbuchung fehlgeschlagen. Bitte anderen Slot wählen.');
      loadSlots(selDate, selTrainer);
    } finally {
      setConfirming(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#0f0f0f] text-white flex justify-center px-4 py-8">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-6">
          <span className="text-[#C9A227] font-black text-2xl tracking-tight">STRONGFIT</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (error) {
    return (
      <Shell>
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 text-center" data-testid="reschedule-error">
          <AlertTriangle className="w-10 h-10 text-orange-400 mx-auto mb-3" />
          <p>{error}</p>
        </div>
      </Shell>
    );
  }
  if (!info) {
    return <Shell><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" /></div></Shell>;
  }
  if (done || info.status === 'done') {
    return (
      <Shell>
        <div className="bg-[#1a1a1a] border border-green-800 rounded-xl p-6 text-center" data-testid="reschedule-success">
          <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-2">Termin umgebucht!</h1>
          {done ? (
            <p className="text-gray-300">
              Neuer Termin: <span className="text-white font-semibold">{done.new_date} um {done.new_time} Uhr</span>
              {done.trainer_name && <> bei {done.trainer_name}</>}
            </p>
          ) : (
            <p className="text-gray-300">Dieser Termin wurde bereits erfolgreich umgebucht.</p>
          )}
          <p className="text-gray-500 text-sm mt-3">Du erhältst eine Bestätigung. Bis bald! 💪</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-5 mb-4" data-testid="reschedule-info">
        <div className="flex items-start gap-3">
          <CalendarClock className="w-6 h-6 text-orange-400 mt-0.5 shrink-0" />
          <div>
            <h1 className="text-lg font-bold">Hallo {info.customer_first_name}, dein Termin muss verschoben werden</h1>
            <p className="text-gray-400 text-sm mt-1">
              <span className="line-through">{info.service_name} am {info.old_date} um {info.old_time} Uhr</span>
              {info.reason && <> — Grund: {info.reason}</>}
            </p>
            <p className="text-gray-300 text-sm mt-2">Wähle unten einfach deinen neuen Wunschtermin:</p>
          </div>
        </div>
      </div>

      {trainers.length > 1 && (
        <div className="mb-4">
          <p className="text-sm text-gray-400 mb-2">Trainer</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelTrainer('any')}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selTrainer === 'any' ? 'bg-[#C9A227] text-black border-[#C9A227] font-semibold' : 'border-[#333] text-gray-300 hover:border-[#C9A227]'}`}
              data-testid="reschedule-trainer-any"
            >
              Egal
            </button>
            {trainers.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelTrainer(t.id)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selTrainer === t.id ? 'bg-[#C9A227] text-black border-[#C9A227] font-semibold' : 'border-[#333] text-gray-300 hover:border-[#C9A227]'}`}
                data-testid={`reschedule-trainer-${t.id}`}
              >
                {t.first_name} {t.last_name}
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-sm text-gray-400 mb-2">Datum</p>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="reschedule-date-strip">
        {nextDays().map((d) => {
          const v = ymd(d);
          return (
            <button
              key={v}
              onClick={() => setSelDate(v)}
              className={`shrink-0 w-14 py-2 rounded-lg border text-center transition-colors ${selDate === v ? 'bg-[#C9A227] text-black border-[#C9A227] font-semibold' : 'border-[#333] text-gray-300 hover:border-[#C9A227]'}`}
              data-testid={`reschedule-date-${v}`}
            >
              <div className="text-[10px] uppercase">{WEEKDAYS[d.getDay()]}</div>
              <div className="text-lg leading-none">{d.getDate()}</div>
              <div className="text-[10px]">{d.toLocaleDateString('de-CH', { month: 'short' })}</div>
            </button>
          );
        })}
      </div>

      {selDate && (
        <>
          <p className="text-sm text-gray-400 mb-2">Freie Zeiten</p>
          {slotsLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" /></div>
          ) : slots && slots.length === 0 ? (
            <p className="text-gray-500 text-sm py-4" data-testid="reschedule-no-slots">An diesem Tag ist leider nichts frei — bitte wähle einen anderen Tag.</p>
          ) : (
            <div className="grid grid-cols-4 gap-2 mb-5" data-testid="reschedule-slots">
              {(slots || []).map((s, i) => {
                const label = s.time;
                const isSel = selSlot === s;
                return (
                  <button
                    key={i}
                    onClick={() => setSelSlot(s)}
                    className={`py-2 rounded-lg border text-sm transition-colors ${isSel ? 'bg-[#C9A227] text-black border-[#C9A227] font-bold' : 'border-[#333] text-gray-200 hover:border-[#C9A227]'}`}
                    data-testid={`reschedule-slot-${label}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {selSlot && (
        <Button
          onClick={confirm}
          disabled={confirming}
          className="w-full bg-[#C9A227] hover:bg-[#b89220] text-black font-bold py-6 text-base"
          data-testid="reschedule-confirm-btn"
        >
          {confirming ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Neuen Termin bestätigen'}
        </Button>
      )}
    </Shell>
  );
}
